import moment from 'moment';
import * as types from './types';
import { uiActions } from 'state/modules/UI';
import { getErrorMessage, parsePagination } from 'utils';
import * as dashboardActions from 'services/api/dashboard';
import * as reservationsActions from 'services/api/reservations';
import * as vehicleGroupsActions from 'services/api/vehiclesGroups';
import * as qualificationsActions from 'services/api/qualifications';
import * as vehiclesApi from 'services/api/vehicles';
import I18n from 'i18n-js';

const fetchVehicleGroupUsageAggregatesRequest = () => ({
  type: types.FETCH_VEHICLE_GROUP_USAGE_AGGREGATES_REQUEST,
});

const fetchVehicleGroupUsageAggregatesSuccess = (payload) => ({
  type: types.FETCH_VEHICLE_GROUP_USAGE_AGGREGATES_SUCCESS,
  payload,
});

const fetchVehicleGroupUsageAggregatesFailed = () => ({
  type: types.FETCH_VEHICLE_GROUP_USAGE_AGGREGATES_FAILED,
});

export const fetchVehicleGroupUsageAggregates = (params) => {
  return async (dispatch) => {
    dispatch(fetchVehicleGroupUsageAggregatesRequest());

    const response = await vehicleGroupsActions.fetchUsageAggregates(params);

    if (response.ok) {
      return dispatch(fetchVehicleGroupUsageAggregatesSuccess(response.data));
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(fetchVehicleGroupUsageAggregatesFailed());
  };
};

// Get noc aggregates
const fetchAggregatesRequest = () => ({
  type: types.FETCH_VEHICLE_GROUPS_AGGREGATES_REQUEST,
});

const fetchAggregatesSuccess = (payload) => ({
  type: types.FETCH_VEHICLE_GROUPS_AGGREGATES_SUCCESS,
  payload,
});

const fetchAggregatesFailed = (error) => ({
  type: types.FETCH_VEHICLE_GROUPS_AGGREGATES_FAILED,
  error,
});

export const fetchAggregates = (params) => {
  return async (dispatch) => {
    dispatch(fetchAggregatesRequest());

    const response = await dashboardActions.fetchAllAggregates(params);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchAggregatesSuccess({
          data: response.data,
          pagination,
        })
      );
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchAggregatesFailed(error));
  };
};

// Get noc aggregates total
const fetchAggregatesTotalsRequest = () => ({
  type: types.FETCH_AGGREGATES_TOTALS_REQUEST,
});

const fetchAggregatesTotalsSuccess = (payload) => ({
  type: types.FETCH_AGGREGATES_TOTALS_SUCCESS,
  payload,
});

const fetchAggregatesTotalsFailed = (error) => ({
  type: types.FETCH_AGGREGATES_TOTALS_FAILED,
  error,
});

export const fetchAggregatesTotals = (params) => {
  return async (dispatch) => {
    dispatch(fetchAggregatesTotalsRequest());
    const response = await dashboardActions.fetchAllAggregatesTotals(params);

    if (response.ok) {
      return dispatch(fetchAggregatesTotalsSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );
    dispatch(fetchAggregatesTotalsFailed(error));
  };
};

// Get noc usages
const fetchVehicleUsagesRequest = () => ({
  type: types.FETCH_VEHICLE_USAGES_REQUEST,
});

const fetchVehicleUsagesSuccess = (payload) => ({
  type: types.FETCH_VEHICLE_USAGES_SUCCESS,
  payload,
});

const fetchVehicleUsagesFailed = (error) => ({
  type: types.FETCH_VEHICLE_USAGES_FAILED,
  error,
});

export const fetchVehicleUsages = (params) => async (dispatch) => {
  dispatch(fetchVehicleUsagesRequest());
  const response = await dashboardActions.fetchVehicleUsages(params);
  if (response.ok) {
    const pagination = parsePagination(response.headers);
    const data = response.data;
    return dispatch(
      fetchVehicleUsagesSuccess({
        data,
        pagination,
      })
    );
  }

  let error;
  if (response?.data?.key === 'USAGETIMERANGEERROR') {
    error = I18n.t('dashboard.error.USAGETIMERANGEERROR');
  } else {
    error = getErrorMessage(response.data);
  }

  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: error,
    })
  );

  dispatch(fetchVehicleUsagesFailed(error));
};

// Change noc dates
export const changeNocCalendarDates = (payload) => {
  return {
    type: types.CHANGE_NOC_CALENDAR_DATES,
    payload,
  };
};

// Change noc visible times
export const changeNocCalendarVisibleTimes = (payload) => {
  return {
    type: types.CHANGE_NOC_CALENDAR_VISIBLE_TIMES,
    payload,
  };
};

const fetchQualsActionsRequest = () => ({
  type: types.FETCH_QUALS_ACTIONS_REQUEST,
});

const fetchQualsActionsSuccess = (payload) => ({
  type: types.FETCH_QUALS_ACTIONS_SUCCESS,
  payload,
});

const fetchQualsActionsFailed = () => ({
  type: types.FETCH_QUALS_ACTIONS_FAILED,
});

export const fetchQualsActions = (params) => {
  return async (dispatch) => {
    dispatch(fetchQualsActionsRequest());
    const response = await qualificationsActions.fetchQualificationActions(params);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchQualsActionsSuccess({
          data: response.data,
          pagination,
        })
      );
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(fetchQualsActionsFailed());
  };
};

const fetchQualsActionsTotalsRequest = () => ({
  type: types.FETCH_QUALS_ACTIONS_TOTALS_REQUEST,
});

const fetchQualsActionsTotalsSuccess = (payload) => ({
  type: types.FETCH_QUALS_ACTIONS_TOTALS_SUCCESS,
  payload,
});

const fetchQualsActionsTotalsFailed = () => ({
  type: types.FETCH_QUALS_ACTIONS_TOTALS_FAILED,
});

export const fetchQualsActionsTotals = (params) => {
  return async (dispatch) => {
    dispatch(fetchQualsActionsTotalsRequest());
    const response = await qualificationsActions.fetchQualificationActionsTotals(params);

    if (response.ok) {
      return dispatch(fetchQualsActionsTotalsSuccess(response.data));
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(fetchQualsActionsTotalsFailed());
  };
};

// Reservation stats actions
const fetchReservationStatsRequest = () => ({
  type: types.FETCH_RESERVATION_STATS_REQUEST,
});

const fetchReservationStatsSuccess = (payload) => ({
  type: types.FETCH_RESERVATION_STATS_SUCCESS,
  payload,
});

const fetchReservationStatsFailed = (error) => ({
  type: types.FETCH_RESERVATION_STATS_FAILED,
  error,
});

export const fetchReservationStats = () => {
  return async (dispatch) => {
    dispatch(fetchReservationStatsRequest());
    const response = await reservationsActions.fetchReservationStats();
    if (response.ok) {
      return dispatch(fetchReservationStatsSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchReservationStatsFailed(error));
  };
};

// Vehicle stats actions
const fetchVehicleStatsRequest = () => ({
  type: types.FETCH_VEHICLE_STATS_REQUEST,
});

const fetchVehicleStatsSuccess = (payload) => ({
  type: types.FETCH_VEHICLE_STATS_SUCCESS,
  payload,
});

const fetchVehicleStatsFailed = (error) => ({
  type: types.FETCH_VEHICLE_STATS_FAILED,
  error,
});

export const fetchVehicleStats = () => {
  return async (dispatch) => {
    dispatch(fetchVehicleStatsRequest());

    const response = await vehiclesApi.fetchVehicleStats();

    if (response.ok) {
      return dispatch(fetchVehicleStatsSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchVehicleStatsFailed(error));
  };
};
